import { Component, computed, effect, inject, OnDestroy, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { ConfirmationService, MessageService, SortEvent } from 'primeng/api';
import { PaginatorState } from 'primeng/paginator';
import { finalize, Subject } from 'rxjs';
import { createDynamicFilterState, DynamicFilterConfig, DynamicFilterValues, EnumValues } from '@card-software/library';
import { AuthService } from '@card-software/nexus-utilities';
import { TranslateService } from '@ngx-translate/core';
import { CommandSearchGreige } from '../../../models/greige/greige-search';
import { Greige } from '../../../models/greige/greige';
import {
  ProductDevelopmentAdminGroup,
  ProductDevelopmentEditorGroup,
  ProductDevelopmentViewerGroup,
} from '../../../models/authorization/enum/user-group.enum';
import { GreigeProxyService } from '../../../proxies/item/greige.proxy.service';
import { EnumService } from '../../../services/enum.service';
import { ProductDevelopmentStatus } from '@app/models/enums/product-development-status';

@Component({
  selector: 'app-greiges',
  templateUrl: './greiges.component.html',
  styleUrl: './greiges.component.scss',
})
export class GreigesComponent implements OnDestroy {
  private router = inject(Router);
  private greigeProxyService = inject(GreigeProxyService);
  private enumService = inject(EnumService);
  private messageService = inject(MessageService);
  private confirmationService = inject(ConfirmationService);
  private translateService = inject(TranslateService);

  private destroy$ = new Subject<void>();
  private lastSearchCriteria: CommandSearchGreige = {};

  isLoading = signal(0);
  filterConfig = signal<DynamicFilterConfig | null>(null);
  readonly filterState = createDynamicFilterState({
    storageKey: 'greiges-filters',
    config: computed(() => (this.enumsReady() ? this.filterConfig() : null)),
  });
  showFilters = signal<boolean>(true);

  // Pagination
  currentPage = signal(1);
  pageSize = signal(25);
  totalRecords = signal(0);

  // Sorting
  sortField = signal<string | undefined>(undefined);
  sortOrder = signal<number>(-1);

  // Data
  listGreiges = signal<Greige[]>([]);
  statuses = signal<EnumValues>({});
  enumsReady = signal(false);

  canEdit = computed(() => {
    if (AuthService.hasRole(ProductDevelopmentAdminGroup.GreigeAdmin) || AuthService.hasRole(ProductDevelopmentEditorGroup.GreigeEditor)) {
      return true;
    }
    if (AuthService.hasRole(ProductDevelopmentViewerGroup.GreigeViewer)) {
      return false;
    }
    return false;
  });

  getRowData(rowData: unknown): Greige {
    return rowData as Greige;
  }

  constructor() {
    this.filterState.applied$.pipe(takeUntilDestroyed()).subscribe((filters) => this.searchWithFilters(filters));

    effect(
      () => {
        const statusesValue = this.enumService.getEnum('ProductDevelopmentStatus', 'itemEnums').values;
        if (Object.keys(statusesValue).length > 0) {
          this.statuses.set(statusesValue);
          this.enumsReady.set(true);
        }
      },
      { allowSignalWrites: true },
    );

    effect(
      () => {
        if (this.translateService.currentLang) {
          this.tryInitializeFilterConfig();
        }
      },
      { allowSignalWrites: true },
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private startLoading(): void {
    this.isLoading.update((val) => val + 1);
  }

  private stopLoading(): void {
    this.isLoading.update((val) => val - 1);
  }

  private tryInitializeFilterConfig(): void {
    if (this.translateService.currentLang) {
      this.initializeFilterConfig();
    }
  }

  private initializeFilterConfig(): void {
    const translate = (key: string, fallback: string) => {
      const translation = this.translateService.instant(key);
      return translation === key ? fallback : translation;
    };

    const statusOptions = Object.entries(this.statuses()).map(([key, value]) => ({
      value: key,
      label: value,
    }));

    const config: DynamicFilterConfig = {
      persistInUrl: true,
      persistVisibility: true,
      filters: [
        {
          type: 'text',
          formControlName: 'greigeCode',
          label: translate('Greige_Code', 'Code'),
          placeholder: translate('Enter_greige_code', 'Enter greige code'),
          defaultVisible: true,
        },
        {
          type: 'text',
          formControlName: 'longDescription',
          label: translate('Greige_Description', 'Description'),
          placeholder: translate('Enter_greige_description', 'Enter description'),
          defaultVisible: true,
        },
        {
          type: 'multiselect',
          formControlName: 'statuses',
          label: translate('Greige_Status', 'Status'),
          placeholder: translate('Select_status', 'Select status'),
          options: statusOptions,
          showClear: true,
          defaultVisible: false,
        },
      ],
    };

    this.filterConfig.set(config);
  }

  onSearch(searchCriteria: CommandSearchGreige): void {
    this.lastSearchCriteria = searchCriteria;
    this.startLoading();
    this.greigeProxyService
      .search(searchCriteria, this.currentPage(), this.pageSize(), this.sortField(), this.sortOrder())
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => {
          this.listGreiges.set(response?.data?.items || []);
          this.totalRecords.set(response?.data?.totalCount || 0);
        },
        error: () => {
          this.listGreiges.set([]);
          this.totalRecords.set(0);
        },
      });
  }

  onPageChange(event: PaginatorState): void {
    this.currentPage.set((event.page || 0) + 1);
    this.pageSize.set(event.rows || this.pageSize());
    this.onSearch(this.lastSearchCriteria);
  }

  onSort(event: SortEvent): void {
    this.sortField.set(event.field);
    this.sortOrder.set(event.order ?? -1);
    this.currentPage.set(1);
    this.onSearch(this.lastSearchCriteria);
  }

  private searchWithFilters(values: DynamicFilterValues): void {
    const filters = values as CommandSearchGreige;
    const searchCriteria: CommandSearchGreige = {
      ...filters,
      statuses: filters.statuses?.map((status) => Number(status)),
    };
    this.currentPage.set(1);
    this.onSearch(searchCriteria);
  }

  goToDetails(id?: string): void {
    const queryParams = id ? { id: id } : {};
    this.router.navigate(['/greige-details'], { queryParams });
  }

  deletePopup(event: Event, greige: Greige) {
    event.stopPropagation();
    this.confirmationService.confirm({
      key: 'deleteGreige',
      target: event.target as EventTarget,
      message: `${this.translateService.instant('Greige_Delete_Modal')} ${greige.greigeCode}?`,
      header: this.translateService.instant('Common_Header_Dialog_Danger'),
      icon: 'pi pi-info-circle',
      rejectLabel: this.translateService.instant('Common_Cancel'),
      rejectButtonProps: {
        label: this.translateService.instant('Common_Cancel'),
        severity: 'secondary',
        outlined: true,
      },
      acceptButtonProps: {
        label: this.translateService.instant('Common_Delete'),
        severity: 'danger',
      },

      accept: () => {
        this.deleteGreige(greige);
      },
    });
  }

  deleteGreige(greige: Greige) {
    this.greigeProxyService.updateGreige(greige.id!, { ...greige, status: 98 }).subscribe({
      next: (response) => {
        if (response.success) {
          this.listGreiges.update((currentGreiges) => currentGreiges.filter((g) => g.id !== greige.id));

          this.messageService.add({
            severity: 'success',
            summary: `${this.translateService.instant('Greige_Delete_Success').replace('{{name}}', greige?.greigeCode || '')}`,
            key: 'br',
            life: 2500,
          });
        }
      },
    });
  }

  canDelete(greige: Greige): boolean {
    return this.canEdit() && greige.status === ProductDevelopmentStatus.Draft;
  }

  getLoopLengthsDisplay(greige: Greige): string {
    if (!greige.yarnDetails || greige.yarnDetails.length === 0) {
      return '[]';
    }

    const loopLengths = greige.yarnDetails
      .map((yarn) => yarn.loopLength)
      .filter((loopLength) => loopLength !== null && loopLength !== undefined);

    return `[${loopLengths.join(', ')}]`;
  }
}
